/**
 * Who made the aeroplanes, read from the licence files rather than typed out again.
 *
 * All three hero models are CC BY 4.0, which means attribution is a *condition of use* and not a
 * courtesy — so the one thing that must never happen is the credits screen drifting from what
 * `models-in/<id>/license.txt` records. Retyping the artist's name into a TypeScript array is
 * exactly how that happens, so the licence files are imported as text and parsed at build time:
 * there is one copy of the fact, it lives next to the model it describes, and changing the model
 * without changing the credit is not possible.
 *
 * `?raw` inlines the file at build time, so this costs three short strings in the bundle and no
 * requests at runtime. The GLBs beside them are not imported and stay lazy — see
 * `render/aircraftModel.ts`.
 *
 * `updateFixed` / `updateFrame`: neither. Parsed once at module load.
 */

import sparrowLicence from '../../models-in/sparrow/license.txt?raw';
import learjetLicence from '../../models-in/learjet/license.txt?raw';
import warbirdLicence from '../../models-in/warbird/license.txt?raw';
import tumblerLicence from '../../models-in/tumbler/license.txt?raw';
import vandalLicence from '../../models-in/vandal/license.txt?raw';

export interface ModelCredit {
  /** The OpenSkies aircraft id the model is used for. */
  id: string;
  /** The work's title, as the artist published it. */
  title: string;
  /** The artist, as the artist published it. */
  artist: string;
  /** The original page. CC BY wants a link to the work, not to the mirror it was fetched from. */
  url: string;
  /** The licence's short name, e.g. "CC BY 4.0". */
  licence: string;
  licenceUrl: string;
  /** The attribution line, verbatim from the licence file. */
  line: string;
}

const SOURCES: readonly (readonly [string, string])[] = [
  ['sparrow', sparrowLicence],
  ['tumbler', tumblerLicence],
  ['vandal', vandalLicence],
  ['warbird', warbirdLicence],
  ['learjet', learjetLicence],
];

/**
 * Pull one `Key: value` field out of a licence file.
 *
 * Throws rather than returning a default: a missing artist is a licence violation waiting to be
 * shipped, and it should stop the build rather than render an empty line nobody notices.
 */
function field(text: string, key: string, id: string): string {
  const match = new RegExp(`^${key}:\\s*(.+)$`, 'm').exec(text);
  if (match === null) throw new Error(`models-in/${id}/license.txt has no "${key}:" field`);
  return match[1].trim();
}

/** "Creative Commons Attribution 4.0 International (CC BY 4.0)" → "CC BY 4.0". */
function shortLicence(full: string): string {
  const parenthesised = /\(([^)]+)\)/.exec(full);
  return parenthesised === null ? full : parenthesised[1];
}

/**
 * The attribution sentence the licence file itself states.
 *
 * Taken verbatim rather than composed, because composing it means choosing how to render the
 * artist's name — and `Artist: Sousinho (@sousinho)` composes to a sentence that names them
 * differently from the one they wrote. The stated line is the one to reproduce; everything else
 * here is for the UI's benefit and is checked against it.
 */
function attributionLine(text: string, id: string): string {
  const match = /^Attribution:\s*\n+(.+)$/m.exec(text);
  if (match === null) throw new Error(`models-in/${id}/license.txt has no "Attribution:" block`);
  return match[1].trim();
}

export const MODEL_CREDITS: readonly ModelCredit[] = SOURCES.map(([id, text]) => {
  const title = field(text, 'Title', id);
  const line = attributionLine(text, id);
  // The name as the attribution sentence renders it, which is the name to show. The `Artist:`
  // field may carry a handle alongside it; the sentence is what the artist asked for.
  const named = new RegExp(`^"${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}" by (.+?) is licensed`).exec(line);
  if (named === null) {
    throw new Error(`models-in/${id}/license.txt: the attribution line does not name "${title}"`);
  }
  return {
    id,
    title,
    artist: named[1],
    url: field(text, 'Original source', id),
    licence: shortLicence(field(text, 'License', id)),
    licenceUrl: field(text, 'License URL', id),
    line,
  };
});
